import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChange,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { isEqual } from '@fi-shared/utils';
import { isDefined } from '@uirouter/core';
import { ChecklistReportPastChecklistChartSegmentIndex } from '../../../enums/fi-checklist2-report-chart-index-enum';
import { IChecklistStatusProgressData } from '@fi-shared/checklist/checklist-v2';
import { IReportChartData } from '@fi-shared/fi-report-pie-chart/fi-report-pie-chart';

@Component({
    selector: 'fi-history-chart',
    templateUrl: './history-chart.component.html',
    styleUrls: ['./history-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-history-chart' },
    standalone: false,
})
export class FiHistoryChartComponent implements OnInit, OnChanges {
    @Input({ required: true }) statusProgressData: IChecklistStatusProgressData;
    @Input() isDetailedData: boolean;
    @Input() isChartInteractive: boolean;
    @Input() isChartSegmentsInteractive: boolean;
    @Input() isSegmentPercentageValueDisplayed: boolean;
    @Input() isChartLabelHidden: boolean;
    @Output() onChartSegmentClick = new EventEmitter<ChecklistReportPastChecklistChartSegmentIndex>();
    get totalItemsCount(): number {
        return this.completedCount + this.inProgressCount + this.notStartedCount;
    }
    get completedCount(): number {
        return this.statusProgressData.completedCount;
    }
    get inProgressCount(): number {
        return this.statusProgressData.inProgressCount;
    }
    get notStartedCount(): number {
        return this.statusProgressData.notStartedCount;
    }
    activeSegmentIndex: number;
    reportChartData: IReportChartData[] = [];

    constructor(private appTextService: AppTextService) {}

    ngOnInit() {
        this.setChartValues();
    }

    ngOnChanges(changes: SimpleChanges) {
        const change: SimpleChange = changes.statusProgressData;
        if (isDefined(change) && !isEqual(change.currentValue, change.previousValue)) this.setChartValues();
    }

    chartSegmentClick(segmentIndex: number) {
        this.activeSegmentIndex = segmentIndex;
        this.onChartSegmentClick.emit(segmentIndex);
    }

    private setChartValues() {
        this.reportChartData = [];

        this.reportChartData[ChecklistReportPastChecklistChartSegmentIndex.Completed] = {
            gradientColorLight: '#29CC6A',
            gradientColorBright: '#18C45D',
            value: this.completedCount,
        };
        this.reportChartData[ChecklistReportPastChecklistChartSegmentIndex.InProgress] = {
            gradientColorLight: '#EF9150',
            gradientColorBright: '#FF8E1C',
            value: this.inProgressCount,
        };
        this.reportChartData[ChecklistReportPastChecklistChartSegmentIndex.NotStarted] = {
            gradientColorLight: '#FF6454',
            gradientColorBright: '#DE3730',
            value: this.notStartedCount,
        };
    }

    isSegmentIndexSelected(index: ChecklistReportPastChecklistChartSegmentIndex): boolean {
        return this.activeSegmentIndex === index;
    }

    getValueLabel(value: number): string {
        const percentageValue: string = `${this.getPercentageValue(value)}%`;
        return this.isDetailedData
            ? `<span class="bold">${percentageValue}</span> (${value}/${this.totalItemsCount})`
            : percentageValue;
    }

    private getPercentageValue(value: number): number {
        return !this.totalItemsCount ? 0 : Math.round((100 * value) / this.totalItemsCount);
    }

    protected readonly ChecklistReportPastChecklistChartSegmentIndex = ChecklistReportPastChecklistChartSegmentIndex;
}
